import { LTTB } from "downsample/methods/LTTB";

export const DEFAULT_MAX_CHART_POINTS = 150;

/**
 * Downsamples an array of chart rows using LTTB (Largest Triangle Three
 * Buckets), which keeps the visually significant points (peaks, dips,
 * trend shape) and drops the rest.
 *
 * LTTB runs over `[rowIndex, referenceValue]` pairs for EACH reference
 * key, and the union of surviving indices selects whole rows. Because
 * whole rows survive, every series stays date-aligned and Recharts
 * shared tooltips keep working; because every series is sampled, an
 * extremum in any series (a competitor spike, an STLY dip) is retained
 * even where the other series are flat. The cap is therefore per-series:
 * the union can exceed `maxPoints` when series disagree about which
 * points matter, bounded by `keys.length × maxPoints`.
 *
 * Arrays already at or under `maxPoints` are returned unchanged (same
 * reference), so small charts pay nothing.
 */
export function downsampleRows<T extends Record<string, unknown>>(
  rows: T[],
  referenceKeys: keyof T | Array<keyof T>,
  maxPoints: number = DEFAULT_MAX_CHART_POINTS
): T[] {
  if (rows.length <= maxPoints || maxPoints < 3) return rows;

  const keys = Array.isArray(referenceKeys) ? referenceKeys : [referenceKeys];
  const keptIndices = new Set<number>();

  for (const key of keys) {
    const indexed: Array<[number, number]> = rows.map((row, i) => {
      const value = row[key];
      return [
        i,
        typeof value === "number" && Number.isFinite(value) ? value : 0,
      ];
    });

    const kept = LTTB(indexed, maxPoints) as Array<[number, number]>;
    for (const [index] of kept) keptIndices.add(index);
  }

  return Array.from(keptIndices)
    .sort((a, b) => a - b)
    .map((index) => rows[index]);
}
